import { motion, AnimatePresence } from 'framer-motion';
import { useReducer } from 'react';
import { createPortal } from 'react-dom';

import * as styles from '@/styles/components/Dialog';

type Dialog = {
  visible: boolean;
  title: string;
  width?: string;
  children: React.ReactNode;
};

type Body = {
  text?: React.ReactNode;
  children?: React.ReactNode;
};

type Footer = {
  children: React.ReactNode;
};

export const useDialog = (initialState = false) => {
  const [visible, toggle] = useReducer((visible) => !visible, initialState);

  return [visible, toggle] as const;
};

const Dialog = ({ visible = false, title, width, children }: Dialog) => {
  if (typeof window === 'undefined') {
    return null;
  } else {
    return createPortal(
      <AnimatePresence>
        {visible && (
          <motion.div
            css={styles.root}
            key='dialog'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}>
            <div css={styles.overlay}></div>
            <div css={styles.content(width)} role='dialog'>
              {title && (
                <div css={styles.header}>
                  <div css={styles.title}>{title}</div>
                </div>
              )}
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  }
};

export const Body = ({ text, children }: Body) => {
  return (
    <div css={styles.body}>
      {text && <div css={styles.text}>{text}</div>}
      {children && <div>{children}</div>}
    </div>
  );
};

export const Footer = ({ children }: Footer) => {
  return <div css={styles.footer}>{children}</div>;
};

Dialog.Body = Body;
Dialog.Footer = Footer;

export default Dialog;
